import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ImageDropzone from "@/components/ImageDropzone";
import { useImageUpload } from "@/hooks/useImageUpload";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const EMPTY = { disease_name: "", diagnosis: "", status: "confirmed", prescription_path: null, prescription_filename: null };

export default function ClinicalNoteDialog({ appointment, onOpenChange, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const upload = useImageUpload({
    onUploaded: (data, file) => { setForm((f) => ({ ...f, prescription_path: data.path, prescription_filename: data.filename })); setPreview(URL.createObjectURL(file)); },
  });

  useEffect(() => {
    if (!appointment) return;
    setForm({
      disease_name: appointment.disease_name || "", diagnosis: appointment.diagnosis || "",
      status: appointment.status === "completed" ? "completed" : "confirmed",
      prescription_path: appointment.prescription_path, prescription_filename: appointment.prescription_filename,
    });
    setPreview(appointment.prescription_path ? "existing" : null);
  }, [appointment]);

  const submit = async () => {
    setSaving(true);
    try {
      await api.patch(`/appointments/${appointment.id}/clinical`, form);
      toast.success("Clinical notes saved");
      onOpenChange(false);
      onSaved();
    } catch (e) { toast.error(apiError(e, "Could not save notes")); }
    finally { setSaving(false); }
  };

  if (!appointment) return null;
  const previewNode = preview === "existing"
    ? <div className="h-28 w-40 rounded-lg bg-teal-50 border border-teal-200 flex items-center justify-center text-sm text-teal-700">Image attached</div>
    : preview && <img src={preview} alt="prescription" className="h-28 w-40 object-cover rounded-lg border" />;

  return (
    <Dialog open={!!appointment} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="clinical-note-dialog">
        <DialogHeader>
          <DialogTitle className="font-head text-2xl">Clinical Notes</DialogTitle>
          <DialogDescription>{appointment.patient_name} · {appointment.patient_age} yrs · {appointment.receipt_no}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Disease / Condition</Label>
            <Input data-testid="clinical-disease-input" value={form.disease_name} onChange={(e) => set("disease_name", e.target.value)} placeholder="e.g. Hypertension" />
          </div>
          <div className="space-y-2">
            <Label>Diagnosis & Advice</Label>
            <Textarea data-testid="clinical-diagnosis-input" rows={4} value={form.diagnosis} onChange={(e) => set("diagnosis", e.target.value)} placeholder="Clinical notes, medicines, follow-up…" />
          </div>
          <div className="space-y-2">
            <Label>Visit Status</Label>
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger data-testid="clinical-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="confirmed">Confirmed (in progress)</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Prescription Image</Label>
            <ImageDropzone upload={upload} testid="clinical-prescription-upload" label="Upload prescription photo" preview={previewNode}
              onClear={() => { setForm((f) => ({ ...f, prescription_path: null, prescription_filename: null })); setPreview(null); }} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="clinical-save-button" onClick={submit} disabled={saving} className="bg-teal-600 hover:bg-teal-700 text-white">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Notes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
